<script lang="ts">
	import { consolidatedDividends } from '$lib/utils/dividends';
	import dayjs from 'dayjs';
	import * as ss from 'simple-statistics';

	interface Props {
		symbol: Populated<BaseSymbol, 'sgxSecurity'>;
		dividends: Populated<BaseDividend, never>[] | undefined;
		dividendYieldHistory: DividendYieldHistory | undefined;
		dividendPayout: DividendPayoutYear | undefined;
		dividendPayoutRatio: DividendPayoutRatioYear | undefined;
		forwardDividendYield: number | undefined;
		ttmDividendYield: number;
	}

	let {
		symbol,
		dividends,
		dividendYieldHistory,
		dividendPayout,
		dividendPayoutRatio,
		forwardDividendYield,
		ttmDividendYield
	}: Props = $props();

	let upcomingDividend: Populated<BaseDividend, never> | undefined = $state();
	let previousDividend: Populated<BaseDividend, never> | undefined = $state();
	let currentDividendYield: number | undefined = $state();
	let meanDividendYield: number | undefined = $state();

	$effect(() => {
		if (dividends) {
			upcomingDividend = consolidatedDividends(dividends)
				.filter(({ paymentDate }) => dayjs().isBefore(paymentDate, 'day'))
				.pop();
			previousDividend = consolidatedDividends(dividends).filter(({ paymentDate }) =>
				dayjs(paymentDate).isBefore(dayjs(), 'day')
			)[0];
		}
	});

	$effect(() => {
		if (dividendYieldHistory) {
			currentDividendYield = dividendYieldHistory[dividendYieldHistory.length - 1].dividendYield;
			meanDividendYield = ss.mean(dividendYieldHistory.map(({ dividendYield }) => dividendYield));
		}
	});
</script>

<div class="mt-6 max-w-screen-sm">
	{#if dividends?.length}
		<div class="card-border p-5">
			<dl class="card-regular [&_dd]:card-bold grid grid-cols-[auto_auto] gap-3 [&_dd]:text-right">
				{#if symbol.sgxSecurity.securityType === 'etfs'}
					<dt>Dividend Yield (TTM)</dt>
					<dd>
						{ttmDividendYield.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 1,
							maximumFractionDigits: 1
						})}
					</dd>
				{/if}
				{#if forwardDividendYield}
					<dt>Forward dividend yield</dt>
					<dd>
						{forwardDividendYield.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 1,
							maximumFractionDigits: 1
						})}
					</dd>
				{/if}
				{#if currentDividendYield}
					<dt>Current dividend yield</dt>
					<dd>
						{currentDividendYield.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 1,
							maximumFractionDigits: 1
						})}
					</dd>
				{/if}
				{#if meanDividendYield}
					<dt>Historical Average Dividend Yield</dt>
					<dd>
						{meanDividendYield.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 1,
							maximumFractionDigits: 1
						})}
					</dd>
				{/if}
				{#if dividendPayout}
					<dt>
						<p class="mb-1">Dividend Per Share</p>
						<p class="text-gray-mid">Year on Year growth</p>
					</dt>
					<dd>
						<p class="mb-1">
							{dividendPayout.total.toLocaleString('default', {
								style: 'currency',
								currency: symbol.currency,
								minimumFractionDigits: 2,
								maximumFractionDigits: 6,
								currencyDisplay: 'narrowSymbol'
							})}
						</p>
						<p class="text-gray-mid">
							{(dividendPayout.percentChange ?? 0).toLocaleString('default', {
								style: 'percent'
							})}
						</p>
					</dd>
				{/if}

				{#if dividendPayoutRatio}
					<dt>Historical Payout Ratio</dt>
					<dd>
						{dividendPayoutRatio.payoutRatio.toLocaleString('default', {
							style: 'percent',
							maximumFractionDigits: 0
						})}
					</dd>
				{/if}

				{#if symbol.dividendFrequency}
					<dt>Historical Dividend Frequency</dt>
					<dd>{symbol.dividendFrequency}</dd>
				{/if}

				{#if upcomingDividend}
					<dt>
						<p class="mb-1">Upcoming dividend</p>
						<p class="text-gray-mid">
							Ex-date on {dayjs(upcomingDividend.exDate).format('DD MMM YYYY')}
							<br />
							Pay-date on {dayjs(upcomingDividend.paymentDate).format('DD MMM YYYY')}
							<br />
							<a href="/upcoming-dividends" class="hyperlink">View stocks with upcoming dividends</a
							>
						</p>
					</dt>
					<dd>
						{upcomingDividend?.declaredRate.toLocaleString('default', {
							style: 'currency',
							currency: symbol.currency,
							minimumFractionDigits: 2,
							maximumFractionDigits: 6,
							currencyDisplay: 'narrowSymbol'
						})}
					</dd>
				{:else}
					<div class="col-span-2">
						<p class="mb-1">Upcoming Dividend</p>
						<p class="text-gray-mid">No upcoming dividend announced</p>
						<p>
							<a href="/upcoming-dividends" class="hyperlink">View stocks with upcoming dividends</a
							>
						</p>
					</div>
				{/if}
				<dt>
					<p class="mb-1">Previous dividend</p>
					<p class="text-gray-mid">
						Paid on {dayjs(previousDividend?.paymentDate).format('DD MMM YYYY')}
					</p>
				</dt>
				<dd>
					{previousDividend?.declaredRate.toLocaleString('default', {
						style: 'currency',
						currency: symbol.currency,
						minimumFractionDigits: 2,
						maximumFractionDigits: 6,
						currencyDisplay: 'narrowSymbol'
					})}
				</dd>
			</dl>
			<p class="small-bold text-gray-mid mt-5 text-right">
				By SGX as of {dayjs(symbol.sgxSecurity.lastCrawled).format('DD MMM YYYY')}
			</p>
		</div>
	{:else}
		<p class="card-regular text-gray-mid mt-6 text-center lg:text-left">
			No dividend data is currently available for {symbol.name}.
		</p>
	{/if}
</div>
